import React, { useMemo } from 'react';
import { FileText, RefreshCw, XCircle, Send, CheckCircle2, Clock, SkipForward } from 'lucide-react';
import { construirTimelineCobranca } from '../../src/lib/timelineCobranca';
import type { EventoCobranca, EventoCobrancaTipo, FaturaParaTimeline } from '../../src/lib/timelineCobranca';

// Timeline da cobrança — quem emitiu, quem cancelou, quem reemitiu, quem enviou.
//
// Chamado do Ítalo (28/08/2026): o modal mostrava só dois cards planos, "Emitido por"
// e "Enviado por", ambos preenchidos com o nome de quem mexeu por ÚLTIMO. Na fatura
// 9486 isso apagou da tela que o Ítalo tinha emitido o boleto três semanas antes de o
// João cancelar e reemitir. A regra de negócio não mudou (`emitido_por_nome` continua
// sendo o boleto vivo, como decidido em 19/08/2026) — o que faltava era mostrar o
// resto, que já estava gravado e agora sai de `historico_emissoes`.
//
// Desde 07/10/2026 também mostra o pulo de cálculo e o pulo de envio, e separa a
// comunicação sem cobrança do envio do boleto (ver `tipoDoEnvio`).

const ESTILO: Record<EventoCobrancaTipo, {
  titulo: string;
  icone: React.ReactNode;
  ponto: string;
  rotulo: string;
}> = {
  pulo_calculo: {
    titulo: 'Cálculo pulado',
    icone: <SkipForward size={13} className="text-white" />,
    ponto: 'bg-yellow-500',
    rotulo: 'text-yellow-700 dark:text-yellow-400',
  },
  emissao: {
    titulo: 'Boleto emitido',
    icone: <FileText size={13} className="text-white" />,
    ponto: 'bg-[#5F6C37]',
    rotulo: 'text-[#5F6C37] dark:text-[#8ba24e]',
  },
  reemissao: {
    titulo: 'Boleto reemitido',
    icone: <RefreshCw size={13} className="text-white" />,
    ponto: 'bg-amber-500',
    rotulo: 'text-amber-600 dark:text-amber-400',
  },
  cancelamento: {
    titulo: 'Cobrança cancelada',
    icone: <XCircle size={13} className="text-white" />,
    ponto: 'bg-rose-500',
    rotulo: 'text-rose-600 dark:text-rose-400',
  },
  envio: {
    titulo: 'Cobrança enviada ao cliente',
    icone: <Send size={13} className="text-white" />,
    ponto: 'bg-sky-500',
    rotulo: 'text-sky-600 dark:text-sky-400',
  },
  comunicado: {
    titulo: 'Comunicação enviada (sem cobrança)',
    icone: <Send size={13} className="text-white" />,
    ponto: 'bg-yellow-500',
    rotulo: 'text-yellow-700 dark:text-yellow-400',
  },
  pulo_envio: {
    titulo: 'Envio pulado (nada foi disparado)',
    icone: <SkipForward size={13} className="text-white" />,
    ponto: 'bg-gray-400',
    rotulo: 'text-gray-600 dark:text-gray-300',
  },
  pagamento: {
    titulo: 'Pagamento confirmado',
    icone: <CheckCircle2 size={13} className="text-white" />,
    ponto: 'bg-emerald-500',
    rotulo: 'text-emerald-600 dark:text-emerald-400',
  },
};

const formatarQuando = (iso: string) => {
  const data = new Date(iso);
  return `${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const LinhaEvento: React.FC<{ evento: EventoCobranca; ultimo: boolean }> = ({ evento, ultimo }) => {
  const estilo = ESTILO[evento.tipo];

  return (
    <li className="relative pl-9 pb-4 last:pb-0">
      {!ultimo && (
        <span
          className="absolute left-[10px] top-6 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
          aria-hidden="true"
        />
      )}
      <span className={`absolute left-0 top-0.5 w-[21px] h-[21px] rounded-full flex items-center justify-center shadow-sm ${estilo.ponto}`}>
        {estilo.icone}
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={`text-sm font-bold ${estilo.rotulo}`}>{estilo.titulo}</span>
        {evento.numCobranca && (
          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-mono">
            nº {evento.numCobranca}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
        {evento.tipo === 'pagamento' ? (
          <span className="text-gray-500 dark:text-gray-400">{formatarQuando(evento.em)}</span>
        ) : (
          <>
            por <span className="font-semibold text-gray-900 dark:text-white">{evento.por}</span>
            <span className="text-gray-400 dark:text-gray-500"> · {formatarQuando(evento.em)}</span>
          </>
        )}
      </p>

      {evento.detalhe && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">"{evento.detalhe}"</p>
      )}
    </li>
  );
};

interface TimelineCobrancaProps {
  fatura: FaturaParaTimeline | null | undefined;
  /** Ordem cronológica reversa — usada onde a tela é sobre o estado atual. */
  maisRecentePrimeiro?: boolean;
  className?: string;
}

const TimelineCobranca: React.FC<TimelineCobrancaProps> = ({ fatura, maisRecentePrimeiro = false, className = '' }) => {
  const eventos = useMemo(() => {
    const lista = construirTimelineCobranca(fatura);
    return maisRecentePrimeiro ? [...lista].reverse() : lista;
  }, [fatura, maisRecentePrimeiro]);

  if (eventos.length === 0) return null;

  const reemissoes = eventos.filter(e => e.tipo === 'reemissao').length;

  return (
    <div className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <Clock size={14} className="text-primary" />
          Histórico da cobrança
        </p>
        {reemissoes > 0 && (
          <span className="px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/20 text-[10px] font-bold uppercase tracking-wider">
            {reemissoes === 1 ? '1 reemissão' : `${reemissoes} reemissões`}
          </span>
        )}
      </div>

      <ol className="list-none">
        {eventos.map((evento, indice) => (
          <LinhaEvento
            key={`${evento.tipo}-${evento.ordem}-${indice}`}
            evento={evento}
            ultimo={indice === eventos.length - 1}
          />
        ))}
      </ol>
    </div>
  );
};

export default TimelineCobranca;
